import * as Blockly from 'blockly/core'

import { ObjectRuntimeState, Quat, RobloxMaterial } from '../../types'
import { quatToRobloxOrientation } from '../quat'
import { BRICK_COLOR_HEX, toRobloxMaterial } from '../robloxValues'
import { PreviewValue, toNumber } from './values'

/**
 * 値ブロック評価器。数値・文字列・真偽値・色・マテリアルの値ブロックを
 * 相互再帰で評価する。Interpreter（文ハンドラ）から参照される一方向の依存で、
 * 共有状態は Interpreter からコンストラクタで注入される:
 * - variables: スクリプトローカル変数の Map（Interpreter と参照を共有）
 * - selfState / currentOrientation: 文ハンドラ側にも残る状態アクセサ
 * - getPlayStartMs: beginRun で再代入されるためアクセサで注入
 */
export class ExpressionEvaluator {
  constructor(
    private readonly variables: Map<string, PreviewValue>,
    private readonly selfState: () => ObjectRuntimeState | undefined,
    private readonly currentOrientation: (state: ObjectRuntimeState) => Quat,
    private readonly getPlayStartMs: () => number,
  ) {}

  /**
   * 値ブロックを PreviewValue（数値 or 文字列）として評価する。text / 文字列連結 /
   * 文字列変数は文字列、それ以外は数値。setVariableTo の値保持に使う。
   */
  evaluateValue(block: Blockly.Block | null): PreviewValue {
    if (!block || !block.isEnabled()) {
      return 0
    }
    if (
      block.type === 'text' ||
      block.type === 'operator_concatenate' ||
      block.type === 'looks_brickColors' ||
      block.type === 'looks_brickColor' ||
      block.type === 'looks_materials' ||
      block.type === 'looks_material'
    ) {
      return this.evaluateString(block)
    }
    if (block.type === 'variable_getVariable') {
      const name = block.getField('VAR')?.getText()
      return typeof name === 'string' ? (this.variables.get(name) ?? 0) : 0
    }
    return this.evaluateNumber(block)
  }

  /**
   * looks_setBrickColorTo の入力を CSS 16 進カラーへ評価する。
   * - looks_brickColors（ドロップダウン）→ パレット色
   * - looks_brickColor（現在色のレポーター）→ 現在の色
   */
  evaluateColor(block: Blockly.Block | null): string | null {
    if (!block || !block.isEnabled()) {
      return null
    }
    if (block.type === 'looks_brickColors') {
      const name = block.getFieldValue('VAL')
      return typeof name === 'string' ? (BRICK_COLOR_HEX[name] ?? null) : null
    }
    if (block.type === 'looks_brickColor') {
      return this.selfState()?.color ?? null
    }
    console.warn(`[preview] 未対応の色ブロック: ${block.type} (${block.id})`)
    return null
  }

  /**
   * looks_setMaterialTo の入力を描画可能な RobloxMaterial へ評価する。
   * - looks_materials（ドロップダウン）→ プレビュー対応素材への正規化
   * - looks_material（現在マテリアルのレポーター）→ 現在のマテリアル
   */
  evaluateMaterial(block: Blockly.Block | null): RobloxMaterial | null {
    if (!block || !block.isEnabled()) {
      return null
    }
    if (block.type === 'looks_materials') {
      const name = block.getFieldValue('VAL')
      return typeof name === 'string' ? toRobloxMaterial(name) : null
    }
    if (block.type === 'looks_material') {
      return this.selfState()?.material ?? null
    }
    console.warn(
      `[preview] 未対応のマテリアルブロック: ${block.type} (${block.id})`,
    )
    return null
  }

  /**
   * looks_showMessage（print）の入力を文字列へ評価する。
   * text ブロックは文字列、数値ブロックは数値文字列。文字列連結は Phase2。
   */
  evaluateString(block: Blockly.Block | null): string {
    if (!block || !block.isEnabled()) {
      return ''
    }
    if (block.type === 'text') {
      const text = block.getFieldValue('TEXT')
      return typeof text === 'string' ? text : String(text ?? '')
    }
    // 色・マテリアルブロックは名前/現在値を文字列化（print の見た目を実機に寄せる）。
    if (
      block.type === 'looks_brickColors' ||
      block.type === 'looks_brickColor'
    ) {
      return this.evaluateColor(block) ?? ''
    }
    if (block.type === 'looks_materials') {
      const name = block.getFieldValue('VAL')
      return typeof name === 'string' ? name : String(name ?? '')
    }
    if (block.type === 'looks_material') {
      return this.selfState()?.material ?? ''
    }
    // 文字列連結（Lua: v .. v2）。両辺を文字列化して結合する。
    if (block.type === 'operator_concatenate') {
      return (
        this.evaluateString(block.getInputTargetBlock('VAL')) +
        this.evaluateString(block.getInputTargetBlock('VAL2'))
      )
    }
    // 変数は保持している型（数値/文字列）を文字列化する。
    if (block.type === 'variable_getVariable') {
      const name = block.getField('VAR')?.getText()
      return typeof name === 'string' ?
          String(this.variables.get(name) ?? 0)
        : ''
    }
    return String(this.evaluateNumber(block))
  }

  /**
   * 値 input を数値として評価する。
   * 対応: math_number / math_integer（NUM）, math_empty_number_*（VAL）,
   *       operator_add/subtract/multiply/divide/mod/random。
   * 評価できない input は 0 を返す（Lua 生成の空入力フォールバックに合わせる）。
   */
  readNumber(block: Blockly.Block, inputName: string): number {
    const child = block.getInputTargetBlock(inputName)
    return this.evaluateNumber(child)
  }

  /**
   * 値 input を PreviewValue（数値 or 文字列）として評価する。
   * 等価比較で数値強制すると文字列が一律 0 になり `'a' = 'b'` が真になってしまうため、
   * 生成 Lua（型を考慮する `==` / `~=`）と挙動を揃える目的で使う。
   */
  private readValue(block: Blockly.Block, inputName: string): PreviewValue {
    return this.evaluateValue(block.getInputTargetBlock(inputName))
  }

  private evaluateNumber(block: Blockly.Block | null): number {
    // disabled な値ブロックは Lua 生成対象外（空入力フォールバック）に合わせ 0。
    if (!block || !block.isEnabled()) {
      return 0
    }
    switch (block.type) {
      case 'math_number':
      case 'math_integer':
        return this.parseFieldNumber(block, 'NUM')
      case 'math_empty_number_empty_string':
      case 'math_empty_number_zero':
        return this.parseFieldNumber(block, 'VAL')
      case 'operator_add':
        return this.readNumber(block, 'VAL') + this.readNumber(block, 'VAL2')
      case 'operator_subtract':
        return this.readNumber(block, 'VAL') - this.readNumber(block, 'VAL2')
      case 'operator_multiply':
        return this.readNumber(block, 'VAL') * this.readNumber(block, 'VAL2')
      case 'operator_divide': {
        const divisor = this.readNumber(block, 'VAL2')
        if (divisor === 0) {
          return 0
        }
        return this.readNumber(block, 'VAL') / divisor
      }
      case 'operator_mod': {
        const divisor = this.readNumber(block, 'VAL2')
        if (divisor === 0) {
          return 0
        }
        return this.readNumber(block, 'VAL') % divisor
      }
      case 'operator_random': {
        const min = this.readNumber(block, 'VAL')
        const max = this.readNumber(block, 'VAL2')
        const lo = Math.ceil(Math.min(min, max))
        const hi = Math.floor(Math.max(min, max))
        return lo + Math.floor(Math.random() * (hi - lo + 1))
      }
      // --- レポーター（script.Parent.* の現在値）---
      case 'movement_xPosition':
        return this.selfState()?.position.x ?? 0
      case 'movement_yPosition':
        return this.selfState()?.position.y ?? 0
      case 'movement_zPosition':
        return this.selfState()?.position.z ?? 0
      // 向きレポーター（度・Roblox Orientation の YXZ 抽出）。
      case 'movement_xOrientation':
      case 'movement_yOrientation':
      case 'movement_zOrientation': {
        const state = this.selfState()
        if (!state) {
          return 0
        }
        const euler = quatToRobloxOrientation(this.currentOrientation(state))
        if (block.type === 'movement_xOrientation') {
          return euler.x
        }
        if (block.type === 'movement_yOrientation') {
          return euler.y
        }
        return euler.z
      }
      case 'sound_volume':
        // Lua: Sound.Volume * 100。Sound を持たない場合は 0。
        return (this.selfState()?.soundVolume ?? 0) * 100
      case 'looks_xSize':
        return this.selfState()?.size.x ?? 0
      case 'looks_ySize':
        return this.selfState()?.size.y ?? 0
      case 'looks_zSize':
        return this.selfState()?.size.z ?? 0
      // --- 変数 / センサー ---
      case 'variable_getVariable': {
        const name = block.getField('VAR')?.getText()
        return typeof name === 'string' ? toNumber(this.variables.get(name)) : 0
      }
      case 'sensing_timer':
        // Lua: time()。プレビューはプレイ開始からの経過秒で近似する。
        return (performance.now() - this.getPlayStartMs()) / 1000
      default:
        console.warn(
          `[preview] 未対応の数値ブロックを 0 として評価: ${block.type} (${block.id})`,
        )
        return 0
    }
  }

  parseFieldNumber(block: Blockly.Block, fieldName: string): number {
    const raw = block.getFieldValue(fieldName)
    const value = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(value) ? value : 0
  }

  /**
   * 値 input を真偽値として評価する。
   * 対応: 比較演算子・論理演算子。評価できない input は false（Lua の空入力フォールバック）。
   */
  readBoolean(block: Blockly.Block, inputName: string): boolean {
    const child = block.getInputTargetBlock(inputName)
    return this.evaluateBoolean(child)
  }

  evaluateBoolean(block: Blockly.Block | null): boolean {
    // disabled な値ブロックは Lua 生成対象外（空入力フォールバック）に合わせ false。
    if (!block || !block.isEnabled()) {
      return false
    }
    switch (block.type) {
      case 'operator_equals':
        return this.readValue(block, 'VAL') === this.readValue(block, 'VAL2')
      case 'operator_notEquals':
        return this.readValue(block, 'VAL') !== this.readValue(block, 'VAL2')
      case 'operator_lessThan':
        return this.readNumber(block, 'VAL') < this.readNumber(block, 'VAL2')
      case 'operator_greaterThan':
        return this.readNumber(block, 'VAL') > this.readNumber(block, 'VAL2')
      case 'operator_lessThanOrEqual':
        return this.readNumber(block, 'VAL') <= this.readNumber(block, 'VAL2')
      case 'operator_greaterThanOrEqual':
        return this.readNumber(block, 'VAL') >= this.readNumber(block, 'VAL2')
      case 'operator_and':
        return this.readBoolean(block, 'VAL') && this.readBoolean(block, 'VAL2')
      case 'operator_or':
        return this.readBoolean(block, 'VAL') || this.readBoolean(block, 'VAL2')
      case 'operator_not':
        return !this.readBoolean(block, 'VAL')
      default:
        console.warn(
          `[preview] 未対応の真偽ブロックを false として評価: ${block.type} (${block.id})`,
        )
        return false
    }
  }
}
